// Renders mixed text + LaTeX. Inline math wrapped in $…$, display math in $$…$$.
import { useMemo } from 'react';
import katex from 'katex';

interface MathTextProps {
  children?: string | null;
  className?: string;
}

interface Segment {
  type: 'text' | 'inline' | 'display';
  value: string;
}

function tokenize(input: string): Segment[] {
  const segments: Segment[] = [];
  // Match $$…$$ (display) or $…$ (inline). Non-greedy.
  const regex = /\$\$([\s\S]+?)\$\$|\$([^$]+?)\$/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(input)) !== null) {
    if (match.index > last) {
      segments.push({ type: 'text', value: input.slice(last, match.index) });
    }
    if (match[1] !== undefined) {
      segments.push({ type: 'display', value: match[1] });
    } else {
      segments.push({ type: 'inline', value: match[2] });
    }
    last = regex.lastIndex;
  }
  if (last < input.length) {
    segments.push({ type: 'text', value: input.slice(last) });
  }
  return segments;
}

export function MathText({ children, className }: MathTextProps) {
  // Be defensive: a missing/non-string value must never crash the render
  // (e.g. a question still loading or a field the API didn't populate).
  const segments = useMemo(() => tokenize(typeof children === 'string' ? children : children == null ? '' : String(children)), [children]);
  return (
    <span className={className}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
        let html: string;
        try {
          html = katex.renderToString(seg.value, {
            throwOnError: false,
            displayMode: seg.type === 'display',
          });
        } catch {
          html = seg.value;
        }
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </span>
  );
}
